// src/components/Projects.tsx

import React from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Image,
  Stack,
  Tag,
  Container,
  Link,
  Icon,
} from "@chakra-ui/react";
import { FiArrowUpRight } from "react-icons/fi";
import { projects } from "../constants/project";
import { accent, surface, border } from "../constants/constants";
import Reveal from "./base/Reveal";

const Projects: React.FC = () => {
  return (
    <Box
      as="section"
      id="projects"
      aria-labelledby="projects-heading"
      py={{ base: 16, md: 24 }}
      bg="brand.bg"
      scrollMarginTop={{ base: "70px", md: "90px" }}
    >
      <Container maxW="container.xl" px={5}>
        <Reveal>
          <Heading
            id="projects-heading"
            fontSize={{ base: "4xl", md: "6xl" }}
            fontWeight={900}
            textAlign="center"
            color="brand.text"
            mb={20}
          >
            Projects
          </Heading>
        </Reveal>

        <Stack spacing={16}>
          {projects.map((project, index) => (
            <Reveal
              key={project.id}
              direction={index % 2 === 0 ? "left" : "right"}
            >
              <Flex
                role="group"
                direction={{
                  base: "column",
                  md: index % 2 === 0 ? "row" : "row-reverse",
                }}
                align="center"
                bg={surface}
                border="1px solid"
                borderColor={border}
                p={{ base: 5, md: 8 }}
                rounded="2xl"
                overflow="hidden"
                gap={{ base: 6, md: 10 }}
                transition="border-color 0.2s ease, transform 0.2s ease"
                _hover={{ borderColor: accent, transform: "translateY(-4px)" }}
              >
                {/* Image */}
                <Box
                  flexShrink={0}
                  width={{ base: "100%", md: "40%" }}
                  overflow="hidden"
                  rounded="xl"
                >
                  <Image
                    src={project.imageSrc}
                    alt={project.title}
                    objectFit="cover"
                    width="100%"
                    height={{ base: "200px", md: "260px" }}
                    rounded="xl"
                    transition="transform 0.4s ease"
                    _groupHover={{ transform: "scale(1.06)" }}
                  />
                </Box>

                {/* Project Info */}
                <Box flex="1">
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="brand.text"
                    mb={3}
                  >
                    {project.title}
                  </Heading>
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="brand.muted"
                    mb={4}
                  >
                    {project.description}
                  </Text>
                  <Stack direction="row" wrap="wrap" mb={4}>
                    {project.technologies.map((tech) => (
                      <Tag
                        key={tech}
                        size="md"
                        variant="subtle"
                        bg="brand.accentMuted"
                        color={accent}
                        mr={2}
                        mb={2}
                      >
                        {tech}
                      </Tag>
                    ))}
                  </Stack>
                  {project.link && (
                    <Link
                      href={project.link}
                      isExternal
                      fontWeight={700}
                      color="brand.text"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      _hover={{ color: accent, textDecoration: "none" }}
                    >
                      <Icon
                        as={FiArrowUpRight}
                        aria-hidden="true"
                        color={accent}
                        boxSize={5}
                      />
                      View Project
                    </Link>
                  )}
                </Box>
              </Flex>
            </Reveal>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Projects;
